import type { DesiredLocation, ExactLocation } from 'src/features/consumers/schemas/consumer'
import { formatPercentage } from 'src/shared/features/consumers/components/mortgage-calculator/utils'
import { abbreviateMoney } from 'src/shared/utils/abbreviate-money'
import { formatInternationalPhoneNumber } from 'src/shared/utils/format/format-intl-phone-number'
import { formatCurrency } from 'src/shared/utils/format-currency'

import type { BusinessInfo } from '../schemas/business-create'
import { BusinessEnumMap, BusinessInfoFieldsDisplayType, DisplayTypeEnum } from '../schemas/business-create'

const useFormattedFieldValue = (field?: keyof BusinessInfo, value?: any) => {
  const getFormattedValue = (field: keyof BusinessInfo, value: any) => {
    if (!field) return '-'
    if (value === null || value === undefined) return '-'
    if (typeof value === 'string' && value === '') return '-'

    const displayType = BusinessInfoFieldsDisplayType[field]

    if (displayType === DisplayTypeEnum.ENUM) {
      const enumValue = BusinessEnumMap[field as keyof typeof BusinessEnumMap]
      return enumValue[value as keyof typeof enumValue].label
    } else if (displayType === DisplayTypeEnum.RANGE) {
      return `${value.min} - ${value.max}`
    } else if (displayType === DisplayTypeEnum.PRICE_RANGE) {
      return `$${abbreviateMoney(+value.min, 1)} - $${abbreviateMoney(+value.max, 1)}`
    } else if (displayType === DisplayTypeEnum.DATE) {
      return new Date(value).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    } else if (displayType === DisplayTypeEnum.BOOLEAN) {
      return value ? 'Yes' : 'No'
    } else if (displayType === DisplayTypeEnum.DESIRED_LOCATIONS) {
      return value.map((location: DesiredLocation) => location.name).join(', ')
    } else if (displayType === DisplayTypeEnum.EXACT_LOCATIONS) {
      return value.map((location: ExactLocation) => location.propertyStreetAddress1).join(', ')
    } else if (displayType === DisplayTypeEnum.LIST) {
      if (value.length === 0) {
        return '-'
      }
      return value.join(', ')
    } else if (displayType === DisplayTypeEnum.CURRENCY) {
      return `$${abbreviateMoney(+value, 1)}`
    } else if (displayType === DisplayTypeEnum.NUMBER) {
      return formatCurrency(+value)
    } else if (displayType === DisplayTypeEnum.PHONE) {
      return formatInternationalPhoneNumber(value)
    } else if (displayType === DisplayTypeEnum.RATE) {
      return formatPercentage(value)
    }
    return value
  }

  return { value: getFormattedValue(field as keyof BusinessInfo, value), getFormattedValue: getFormattedValue }
}

export default useFormattedFieldValue
